import {
  type ApplicationStatusLabelID,
  type DealsDashboardTab,
  type DealTypeScopeFilter,
  type ExportDealsParams,
  type FilterDataType,
  type GetDealDocumentHTTPHandlerResponse,
  type GetDetailedDealDocumentHTTPHandlerResponse,
  type GetLandlordBuildingsHTTPHandlerResponse,
  type GetLandlordDealsExportHTTPHandlerResponse,
  type LeaseDateFilters,
} from './application.types';
import {
  ApplicationTabId,
  type SortDirection,
  type SortField,
} from './application-query.constants';

import { apiUri } from '@/constants/Constants';
import { DateFormat } from '@/shared/libs/time.lib';
import { DealsDashboardClient } from '@/shared/api';

export const getRenewalFilter = (
  applicationTab: DealsDashboardTab,
  includeExpiredDeals = false,
): DealTypeScopeFilter => {
  if (applicationTab === ApplicationTabId.NewApplications) {
    return { is_renewal: false };
  }

  if (applicationTab === ApplicationTabId.RenewalApplications) {
    return { is_renewal: true };
  }

  if (applicationTab === ApplicationTabId.ExpiringSoon) {
    // Only sent when on: the backend's default already hides the long-expired bonds, and
    // omitting it keeps the payload the same as before the toggle existed.
    return { is_expiring_soon: true, ...(includeExpiredDeals && { include_expired_deals: true }) };
  }

  return {};
};

/**
 * Builds the `deal_status` portion of the search payload. Every status — bond issued
 * included — is a status-pill label id, so `deal_status` is the only status contract.
 */
const buildDealStatusFilters = (
  selectedStatus: FilterDataType[] | undefined,
): { deal_status?: { in: string[] } } => {
  if (!selectedStatus?.length) {
    return {};
  }
  const labelIds = Array.from(
    new Set(
      selectedStatus
        .map((item) => item.labelId)
        .filter((id): id is ApplicationStatusLabelID => Boolean(id)),
    ),
  );
  return labelIds.length ? { deal_status: { in: labelIds } } : {};
};

const createSearchQuery = (
  applicationTab: DealsDashboardTab,
  limit?: number,
  pageCursor?: string | null,
  search?: string,
  selectedStatus?: FilterDataType[],
  selectedBuildings?: FilterDataType[],
  leaseFilter?: LeaseDateFilters,
  sortField?: SortField | null,
  sortDirection?: SortDirection,
  renewalOpportunityStatus?: string[],
  includeExpiredDeals = false,
) => ({
  filters: {
    ...(search && {
      search_text: search
        // remove white spaces on begin and end of search text
        .trim()
        // replace any number of repeated spaces with only one
        .replace(/\s\s+/g, ' '),
    }),
    ...buildDealStatusFilters(selectedStatus),
    ...(renewalOpportunityStatus?.length && {
      renewal_opportunity_status: { in: renewalOpportunityStatus },
    }),
    ...(selectedBuildings?.length && {
      building_id: { in: selectedBuildings.map((item) => item.id) },
    }),
    ...getRenewalFilter(applicationTab, includeExpiredDeals),
    ...(leaseFilter && {
      lease: {
        lease_start_date: {
          from: leaseFilter.startDateRange.from?.format(DateFormat.Calendar),
          to: leaseFilter.startDateRange.to?.format(DateFormat.Calendar),
        },
        lease_end_date: {
          from: leaseFilter.endDateRange.from?.format(DateFormat.Calendar),
          to: leaseFilter.endDateRange.to?.format(DateFormat.Calendar),
        },
      },
    }),
  },
  pagination: {
    ...(limit && { limit }),
    ...(pageCursor && { page_cursor: pageCursor }),
  },
  ...(sortField && sortDirection && { sort: { field: sortField, direction: sortDirection } }),
});

export const getLandlordDeals = async (
  applicationTab: DealsDashboardTab,
  limit: number,
  pageCursor?: string | null,
  search?: string,
  selectedStatus?: FilterDataType[],
  selectedBuildings?: FilterDataType[],
  isFromPagination?: boolean,
  leaseFilter?: LeaseDateFilters,
  sortField?: SortField | null,
  sortDirection?: SortDirection,
  renewalOpportunityStatus?: string[],
  includeExpiredDeals = false,
) => {
  const payloadObject = createSearchQuery(
    applicationTab,
    limit,
    pageCursor,
    search,
    selectedStatus,
    selectedBuildings,
    leaseFilter,
    sortField,
    sortDirection,
    renewalOpportunityStatus,
    includeExpiredDeals,
  );

  const response = await DealsDashboardClient.post(
    isFromPagination ? apiUri.getAllDealsRecordsCount : apiUri.getAllDeals,
    payloadObject,
  );

  return response.data.data;
};

export const exportDeals = async ({
  applicationTab,
  allRecords,
  limit,
  pageCursor,
  search,
  selectedStatus,
  selectedBuildings,
  leaseDates,
  sortField,
  sortDirection,
  renewalOpportunityStatus,
  includeExpiredDeals = false,
}: ExportDealsParams): Promise<GetLandlordDealsExportHTTPHandlerResponse> => {
  let payloadObject;

  if (allRecords) {
    payloadObject = {
      all_records: allRecords,
      queries: {
        filters: {
          ...getRenewalFilter(applicationTab, includeExpiredDeals),
          ...(renewalOpportunityStatus?.length && {
            renewal_opportunity_status: { in: renewalOpportunityStatus },
          }),
        },
      },
    };
  } else {
    payloadObject = {
      all_records: allRecords,
      queries: createSearchQuery(
        applicationTab,
        limit,
        pageCursor,
        search,
        selectedStatus,
        selectedBuildings,
        leaseDates,
        sortField,
        sortDirection,
        renewalOpportunityStatus,
        includeExpiredDeals,
      ),
    };
  }

  const response = await DealsDashboardClient.post<GetLandlordDealsExportHTTPHandlerResponse>(
    apiUri.getAllDealsExport,
    payloadObject,
  );
  return response.data;
};

export const getLandlordBuildings = async (filters: {
  searchText?: string;
  page?: number;
  limit?: number;
}) => {
  const queryParams: Record<string, string | number> = {};
  if (filters.searchText) {
    queryParams.search_text = filters.searchText;
  }
  if (filters.page) {
    queryParams.page = filters.page;
  }
  if (filters.limit) {
    queryParams.limit = filters.limit;
  }
  const response = await DealsDashboardClient.get<GetLandlordBuildingsHTTPHandlerResponse>(
    apiUri.getLandlordBuildings,
    {
      params: queryParams,
    },
  );

  return response.data.data;
};

export const getDealBondDocument = async (dealId: string, source: string) => {
  const queryParams: Record<string, string | number> = {
    document_type: 'signed-bond',
    source,
  };
  const response = await DealsDashboardClient.get<GetDealDocumentHTTPHandlerResponse>(
    apiUri.getDealBondDocument.replace('{dealId}', dealId),
    {
      params: queryParams,
    },
  );

  return response.data.data;
};

export const getDetailedDocument = async (dealId: string, source: string) => {
  const response = await DealsDashboardClient.get<GetDetailedDealDocumentHTTPHandlerResponse>(
    apiUri.getDetailedDealDocument.replace('{dealId}', dealId),
    {
      params: {
        source,
      },
    },
  );

  return response.data.data;
};
